import type { Node } from '../../../core/models/scene';
import type {
  PositionAssignmentType,
  PositionOccupancyVisibility,
  PositionStaffingStatus,
} from '../models/OrgModelProjection';

export const POSITION_STAFFING_CARD_WIDTH = 270;
export const POSITION_STAFFING_CARD_HEIGHT = 136;
export const POSITION_STAFFING_HEADER_HEIGHT = 48;
export const POSITION_STAFFING_MAX_VISIBLE_OCCUPANTS = 2;

export interface PositionStaffingCardOccupant {
  readonly assignmentId: string;
  readonly personId: string;
  readonly displayLabel: string;
  readonly assignmentType: PositionAssignmentType;
  readonly positionId?: string;
}

export interface PositionStaffingCardViewModel {
  readonly title: string;
  readonly department: string | null;
  readonly level: string | null;
  readonly occupancyVisibility: PositionOccupancyVisibility;
  readonly staffingStatus: PositionStaffingStatus;
  readonly occupants: readonly PositionStaffingCardOccupant[];
  readonly visibleOccupants: readonly PositionStaffingCardOccupant[];
  readonly overflowCount: number;
  readonly approvedCount: number;
  readonly staffedCount: number | null;
  readonly vacantCount: number | null;
  readonly successorCount: number | null;
  readonly mutationState: 'idle' | 'pending' | 'conflict';
  readonly scenario: boolean;
}

export interface PositionStaffingCardLayout {
  readonly header: { x: number; y: number; width: number; height: number };
  readonly body: { x: number; y: number; width: number; height: number };
  readonly chips: ReadonlyArray<{
    assignmentId: string;
    chip: { x: number; y: number; width: number; height: number };
    dragHandle: { x: number; y: number; width: number; height: number };
  }>;
  readonly vacancy: {
    chip: { x: number; y: number; width: number; height: number };
  } | null;
}

const ASSIGNMENT_TYPES = new Set<PositionAssignmentType>(['primary', 'secondary', 'acting', 'interim', 'shared']);
const STAFFING_STATES = new Set<PositionStaffingStatus>(['filled', 'covered', 'vacant', 'unknown']);
const ASSIGNMENT_TYPE_PRIORITY: Readonly<Record<PositionAssignmentType, number>> = {
  primary: 0,
  secondary: 1,
  acting: 2,
  interim: 3,
  shared: 4,
  unknown: 5,
};

/** Fail-closed adapter from untrusted scene attrs to renderer-safe staffing data. */
export function toPositionStaffingCardViewModel(node: Node): PositionStaffingCardViewModel {
  const attrs = object(node.attrs);
  const visibility = visibilityValue(attrs.occupancyVisibility);
  const requestedStatus = statusValue(attrs.staffingStatus);
  const staffingStatus = visibility === 'visible' ? requestedStatus : 'unknown';
  const occupants = visibility === 'visible' && Array.isArray(attrs.occupants)
    ? attrs.occupants.map(occupantValue).filter((value): value is PositionStaffingCardOccupant => value !== null)
      .sort(compareOccupants)
    : [];
  const visibleOccupants = occupants.slice(0, POSITION_STAFFING_MAX_VISIBLE_OCCUPANTS);
  return {
    title: text(attrs.title) ?? text(node.text) ?? 'New Position',
    department: text(attrs.department),
    level: text(attrs.level),
    occupancyVisibility: visibility,
    staffingStatus,
    occupants,
    visibleOccupants,
    overflowCount: Math.max(0, occupants.length - visibleOccupants.length),
    approvedCount: positiveInteger(attrs.approvedCount) ?? 1,
    staffedCount: nonnegativeInteger(attrs.staffedCount),
    vacantCount: nonnegativeInteger(attrs.vacantCount),
    successorCount: nonnegativeInteger(attrs.successorCount),
    mutationState: attrs.staffingMutationState === 'pending'
      ? 'pending'
      : attrs.staffingMutationState === 'conflict'
        ? 'conflict'
        : 'idle',
    scenario: attrs.staffingScenario === true,
  };
}

function compareOccupants(left: PositionStaffingCardOccupant, right: PositionStaffingCardOccupant): number {
  return ASSIGNMENT_TYPE_PRIORITY[left.assignmentType] - ASSIGNMENT_TYPE_PRIORITY[right.assignmentType]
    || left.displayLabel.localeCompare(right.displayLabel)
    || left.assignmentId.localeCompare(right.assignmentId);
}

export function positionStaffingCardLayout(
  width: number,
  height: number,
  occupants: readonly PositionStaffingCardOccupant[],
  vacantCount = 0,
): PositionStaffingCardLayout {
  const header = { x: 0, y: 0, width, height: POSITION_STAFFING_HEADER_HEIGHT };
  const body = {
    x: 0,
    y: POSITION_STAFFING_HEADER_HEIGHT,
    width,
    height: Math.max(0, height - POSITION_STAFFING_HEADER_HEIGHT),
  };
  const chipX = 10;
  const chipWidth = Math.max(40, width - chipX * 2);
  const chipHeight = 30;
  const rowHeight = 40;
  const chipStartY = body.y + 5;
  const visibleOccupants = occupants.slice(0, POSITION_STAFFING_MAX_VISIBLE_OCCUPANTS);
  const vacancyRowIndex = visibleOccupants.length;
  return {
    header,
    body,
    chips: visibleOccupants.map((occupant, index) => ({
      assignmentId: occupant.assignmentId,
      chip: {
        x: chipX,
        y: chipStartY + index * rowHeight,
        width: chipWidth,
        height: chipHeight,
      },
      dragHandle: {
        x: chipX,
        y: body.y + index * rowHeight,
        width: 40,
        height: 40,
      },
    })),
    vacancy: vacantCount > 0 && vacancyRowIndex < POSITION_STAFFING_MAX_VISIBLE_OCCUPANTS
      ? {
        chip: {
          x: chipX,
          y: chipStartY + vacancyRowIndex * rowHeight,
          width: chipWidth,
          height: chipHeight,
        },
      }
      : null,
  };
}

export function positionStaffingStatusLabel(status: PositionStaffingStatus): string {
  if (status === 'filled') return 'Filled';
  if (status === 'covered') return 'Covered';
  if (status === 'vacant') return 'Vacant';
  return 'Staffing unavailable';
}

/** Relation-specific status text is omitted entirely when occupancy is not visible. */
export function visiblePositionStaffingStatusLabel(viewModel: PositionStaffingCardViewModel): string | null {
  if (viewModel.occupancyVisibility !== 'visible') return null;
  if (viewModel.approvedCount > 1 && viewModel.staffedCount !== null) {
    return `${viewModel.staffedCount}/${viewModel.approvedCount} filled`;
  }
  return positionStaffingStatusLabel(viewModel.staffingStatus);
}

export function positionStaffingOccupancyAccessibleLabel(
  positionTitle: string,
  occupants: readonly PositionStaffingCardOccupant[],
): string {
  const ordered = [...occupants].sort(compareOccupants);
  if (ordered.length === 0) return `${positionTitle} has no visible occupants.`;
  const visible = ordered.slice(0, POSITION_STAFFING_MAX_VISIBLE_OCCUPANTS);
  const hidden = ordered.slice(POSITION_STAFFING_MAX_VISIBLE_OCCUPANTS);
  const describe = (occupant: PositionStaffingCardOccupant) =>
    `${occupant.displayLabel}, ${occupant.assignmentType} assignment`;
  const visibleText = visible.map(describe).join('; ');
  return hidden.length === 0
    ? `${positionTitle} occupants: ${visibleText}.`
    : `${positionTitle} occupants shown on card: ${visibleText}. ${hidden.length} additional occupant${hidden.length === 1 ? '' : 's'} hidden on the card: ${hidden.map(describe).join('; ')}.`;
}

export function positionStaffingAccent(status: PositionStaffingStatus): string {
  if (status === 'filled') return '#10B981';
  if (status === 'covered') return '#3B82F6';
  if (status === 'vacant') return '#F59E0B';
  return '#94A3B8';
}

function occupantValue(value: unknown): PositionStaffingCardOccupant | null {
  const candidate = object(value);
  const assignmentId = text(candidate.assignmentId);
  const personId = text(candidate.personId);
  const displayLabel = text(candidate.displayLabel);
  const assignmentType = assignmentTypeValue(candidate.assignmentType);
  if (!assignmentId || !personId || !displayLabel || !assignmentType) return null;
  return {
    assignmentId,
    personId,
    displayLabel,
    assignmentType,
    positionId: text(candidate.positionId) ?? undefined,
  };
}

function assignmentTypeValue(value: unknown): PositionAssignmentType | null {
  return typeof value === 'string' && ASSIGNMENT_TYPES.has(value as PositionAssignmentType)
    ? value as PositionAssignmentType
    : null;
}

function visibilityValue(value: unknown): PositionOccupancyVisibility {
  if (value === 'visible' || value === 'partial' || value === 'redacted') return value;
  return 'redacted';
}

function statusValue(value: unknown): PositionStaffingStatus {
  return typeof value === 'string' && STAFFING_STATES.has(value as PositionStaffingStatus)
    ? value as PositionStaffingStatus
    : 'unknown';
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function nonnegativeInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;
}

function positiveInteger(value: unknown): number | null {
  const parsed = nonnegativeInteger(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}
